<template>
  <FeatureGuard feature="solutions">
    <c-box :mt="{ base: '3xl', sm: '4xl' }" :mx="{ base: '-15px', md: '0' }">
      <c-flex :flex-direction="{ base: 'column', md: 'row' }" :mb="{ base: '3xl', md: '4xl' }">
        <c-box :mr="{ md: 'xl', lg: '4xl', '2xl': '6rem' }" flex="1" :mb="{ base: 'xl', md: 0 }">
          <c-box>
            <c-heading
              as="h1"
              :font-size="{ base: '3xl', md: '4xl', xl: '5xl' }"
              data-cy="solutionsBannerHeader"
              >{{ $t("solutions.frontpage.bannerHeader") }}</c-heading
            >
          </c-box>
          <c-box :mt="{ base: '3xl', md: '4xl', lg: '5rem' }">
            <QuickSolutionSearch is-complex></QuickSolutionSearch>
          </c-box>
        </c-box>
        <c-box
          :border-radius="{ base: 'base', '2xl': 'rounder' }"
          :width="{ base: '100%', md: '20rem', lg: '25rem', '2xl': '45rem' }"
          overflow="hidden"
        >
          <c-image
            src="/img/support/solutions/header.png"
            :alt="$t('solutions.frontpage.bannerHeaderImageAlt')"
            mt="-1rem"
            mb="-1rem"
          />
        </c-box>
      </c-flex>

      <c-simple-grid
        :columns="{ base: 1, sm: 2, lg: 4 }"
        spacing="xl"
        :mb="{ base: '3rem', md: '4xl', lg: '6rem' }"
      >
        <c-box v-for="(linkBox, index) in getLinkBoxes()" :key="index" data-cy="solutions-topic">
          <c-box
            v-if="linkBox.disabled"
            flex-direction="column"
            align-items="center"
            justify-content="space-between"
            text-align="center"
            border-radius="base"
            bg="white"
            py="3xl"
            px="xl"
            height="100%"
            opacity="0.6"
            :_hover="{
              opacity: 0.6,
              cursor: 'default',
            }"
          >
            <c-icon
              v-if="linkBox.svgIcon"
              :name="linkBox.svgIcon"
              size="6rem"
              mb="md"
              color="brand.purple"
            />
            <c-image v-else :src="linkBox.imageSrc" mt="md" mb="md" max-width="200px" />
            <c-heading as="h2" font-size="md">{{ linkBox.title }}</c-heading>
          </c-box>
          <c-flex
            v-else
            flex-direction="column"
            align-items="center"
            justify-content="space-between"
            text-align="center"
            border-radius="base"
            bg="white"
            py="3xl"
            px="xl"
            height="100%"
            cursor="pointer"
            @click.prevent.stop="navigate(linkBox.href, linkBox.popup)"
            @keydown.enter="navigate(linkBox.href, linkBox.popup)"
            tabindex="0"
            color="brand.purple"
            :_hover="{
              bg: 'brand.purple',
              color: 'white',
              '& svg': {
                color: 'white',
              },
            }"
            transition="all 0.1s ease-in-out"
          >
            <c-icon
              v-if="linkBox.svgIcon"
              :name="linkBox.svgIcon"
              size="6rem"
              mb="md"
              color="brand.purple"
            />
            <c-image v-else :src="linkBox.imageSrc" mt="md" mb="md" max-width="200px" />
            <c-heading as="h2" font-size="md">{{ linkBox.title }}</c-heading>
          </c-flex>
        </c-box>
      </c-simple-grid>

      <c-simple-grid
        :columns="{ base: 1, md: 3 }"
        :spacing="{ base: 'xl', lg: '32', '2xl': '40' }"
        :mb="{ base: '3xl', md: '4xl' }"
      >
        <LinkListBox
          :title="String($t('solutions.frontpage.linkLists.hotTopicsHeader'))"
          :links="getHotTopicLinks()"
          data-cy="solutions-hot-topics"
        ></LinkListBox>
        <LinkListBox
          :title="String($t('solutions.frontpage.linkLists.howToVideosHeader'))"
          :links="getHowToVideosLinks()"
          data-cy="solutions-how-to-videos"
        ></LinkListBox>
        <LinkListBox
          :title="String($t('solutions.frontpage.linkLists.manualsHeader'))"
          :links="getManualsLinks()"
          data-cy="solutions-manuals"
        ></LinkListBox>
      </c-simple-grid>
      <c-box>
        <c-text>
          <span
            v-html="
              $t('solutions.frontpage.notes.technicalDocuments', { products: getProductsLink() })
            "
          ></span>
        </c-text>
      </c-box>
    </c-box>
  </FeatureGuard>
</template>

<script lang="ts">
import FeatureGuard from "@/components/FeatureGuard.vue";
import { LinkListItem } from "@/components/link-list-item";
import LinkListBox from "@/components/LinkListBox.vue";
import QuickSolutionSearch from "@/components/solutions/QuickSolutionSearch.vue";
import { IRootState } from "@iloq/shared";
import { getIconNameByType } from "@/util/SolutionUtil";
import { CBox, CFlex, CHeading, CIcon, CImage, CSimpleGrid, CText } from "@chakra-ui/vue";
import { defineComponent } from "vue";
import { mapState } from "vuex";

const categoriesPath = "/support/solutions/categories";

interface LinkBox {
  svgIcon?: string;
  imageSrc?: string;
  title: string;
  href: string;
  popup?: boolean;
  disabled?: boolean;
}

export default defineComponent({
  name: "Solutions",
  components: {
    QuickSolutionSearch,
    LinkListBox,
    FeatureGuard,
    CBox,
    CHeading,
    CFlex,
    CImage,
    CSimpleGrid,
    CIcon,
    CText,
  },
  computed: {
    ...mapState({
      user: (state: unknown) => (state as IRootState).user!,
    }),
    ...mapState("solutions", {
      frontPageHowToVideos: (state: any) => state.frontPage.howToVideos,
      allHotTopics: (state: any) => state.hotTopics,
    }),
  },
  mounted() {
    // No better way was found to react to events from $t-based html
    document.getElementById("products-link-1")?.addEventListener("click", (event) => {
      event.preventDefault();
      this.navigate("/products");
    });
  },
  methods: {
    getLinkBoxes(): LinkBox[] {
      return [
        {
          svgIcon: "illustrations_lock_with_key",
          title: this.$t("solutions.frontpage.linkBoxes.qaTopics").toString(),
          href: `${categoriesPath}/80000456124`,
        },
        {
          svgIcon: "illustrations_downloads",
          title: this.$t("solutions.frontpage.linkBoxes.downloads").toString(),
          href: `${categoriesPath}/80000456125`,
        },
        {
          imageSrc: "/img/support/solutions/icons/remote-support.svg",
          title: this.$t("solutions.frontpage.linkBoxes.remoteSupport").toString(),
          href: this.$t("support.remote.linkAddress").toString(),
          popup: true,
        },
        {
          svgIcon: "illustrations_package",
          title: this.$t("solutions.frontpage.linkBoxes.ordersAndReturns").toString(),
          href: `${categoriesPath}/80000456123`,
          disabled: true,
        },
      ];
    },
    getHotTopicLinks(): LinkListItem[] {
      const result: LinkListItem[] = this.allHotTopics.slice(0, 2).map((a: any) => ({
        title: a.title,
        href: `/support/solutions/categories/${a.categoryId}/${a.folderId}/${a.freshdeskId}`,
        icon: getIconNameByType(a),
      }));
      result.push({
        title: this.$t("solutions.frontpage.linkLists.hotTopicsMore").toString(),
        href: "/support/solutions/list/hot-topics",
        icon: "chevron_right",
      });
      return result;
    },
    getHowToVideosLinks(): LinkListItem[] {
      const result: LinkListItem[] = this.frontPageHowToVideos.map((a: any) => ({
        title: a.title,
        href: `/support/solutions/categories/${a.categoryId}/${a.folderId}/${a.freshdeskId}`,
        icon: getIconNameByType(a),
      }));
      result.push({
        title: this.$t("solutions.frontpage.linkLists.howToVideosMore").toString(),
        href: `${categoriesPath}/80000456127`,
        icon: "chevron_right",
      });
      return result;
    },
    getManualsLinks(): LinkListItem[] {
      const lang = this.user.languageCode;
      return [
        {
          title: "iLOQ 5 Series",
          href: `https://support.iloq.com/${lang}/support/solutions/80000455489`,
          icon: "support_doc",
          popup: true,
        },
        {
          title: "iLOQ S10",
          href: `https://support.iloq.com/${lang}/support/solutions/80000455549`,
          icon: "support_doc",
          popup: true,
        },
      ];
    },
    getProductsLink(): string {
      return `<a id="products-link-1" href="#">${this.$t("sidebar.ordering.products.title")}</a>`;
    },
    getTeamViewerLink(): string {
      return `<a href="${this.$t("support.remote.linkAddress")}" target="_blank">TeamViewer</a>`;
    },
    navigate(path: string, popup = false): void {
      if (popup) {
        window.open(path, "_blank");
      } else {
        this.$router.push({ path });
      }
    },
  },
});
</script>
